import React from 'react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Zap, Info, AlertTriangle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface StrategyPresetsProps {
  applyPreset: (type: 'hybrid' | 'martingale' | 'scalping' | 'rebound_shield') => void;
  isRunning?: boolean;
}

export const StrategyPresets: React.FC<StrategyPresetsProps> = ({ applyPreset, isRunning }) => {
  return (
    <div className={`bg-primary/5 border-b border-primary/10 p-5 space-y-4 ${isRunning ? 'opacity-90' : ''}`}>
      {/* {isRunning && (
        <Alert className="bg-amber-50 border-amber-200 shadow-sm animate-in fade-in slide-in-from-top-2 duration-300">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-800 font-black text-xs">
            전략 변경 잠금
          </AlertTitle>
          <AlertDescription className="text-amber-700 text-[10px] font-bold">
            봇이 가동 중이거나 포지션이 있는 상태에서는 전략 프리셋을 적용할 수 없습니다. 설정을 변경하시려면 먼저 봇을 정지해 주세요.
          </AlertDescription>
        </Alert>
      )} */}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Zap className={`w-4 h-4 ${isRunning ? 'text-muted-foreground fill-muted-foreground' : 'text-primary fill-primary'}`} />
          <span className={`text-[11px] font-black uppercase tracking-widest ${isRunning ? 'text-muted-foreground' : 'text-primary'}`}>전략 프리셋 (One-Click Setup)</span>
          {isRunning && <Badge variant="secondary" className="text-[9px] font-black bg-muted/50 text-muted-foreground">가동 중 변경 불가</Badge>}
        </div>
        <Badge variant="outline" className={`text-[9px] font-black px-2 ${isRunning ? 'border-muted text-muted-foreground' : 'border-primary/30 text-primary'}`}>Best for Binance/Bitget</Badge>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Preset 1: Super Hybrid */}
        <div className={`group relative flex flex-col bg-background border rounded-2xl p-4 transition-all ${isRunning ? 'border-muted opacity-80 cursor-not-allowed' : 'border-indigo-500/20 hover:border-indigo-500/50 hover:shadow-lg hover:shadow-indigo-500/5'}`}>
          <div className="flex items-center justify-between mb-3">
            <span className={`text-sm font-black ${isRunning ? 'text-muted-foreground' : 'text-indigo-600'}`}>🏆 슈퍼 하이브리드</span>
            <Button size="sm" variant="ghost" disabled={isRunning} onClick={() => applyPreset('hybrid')} className={`h-7 px-3 rounded-lg font-black text-[10px] ${isRunning ? 'bg-muted text-muted-foreground' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}>
              적용
            </Button>
          </div>
          <div className={`space-y-1.5 border-t pt-3 ${isRunning ? 'border-muted' : 'border-indigo-500/5'}`}>
            <div className="flex items-center gap-1.5 text-[9px] font-bold text-muted-foreground uppercase"><Info className="w-3 h-3 text-indigo-400" /> 설정 변경 내역:</div>
            <ul className="grid grid-cols-1 gap-1">
              <li className="text-[9px] font-medium text-foreground/80 flex items-center gap-1.5">✅ 레버리지 2x (안정성 극대화)</li>
              <li className="text-[9px] font-medium text-foreground/80 flex items-center gap-1.5">✅ 물타기 1.5배 / 10차수</li>
              <li className="text-[9px] font-medium text-foreground/80 flex items-center gap-1.5">✅ 4h 슈퍼 MA + 1h 추세 필터</li>
              <li className="text-[9px] font-medium text-foreground/80 flex items-center gap-1.5">✅ 스토캐스틱 + RSI 정밀 진입</li>
            </ul>
          </div>
        </div>

        {/* Preset 2: Classic Martingale */}
        <div className={`group relative flex flex-col bg-background border rounded-2xl p-4 transition-all ${isRunning ? 'border-muted opacity-80 cursor-not-allowed' : 'border-amber-500/20 hover:border-amber-500/50 hover:shadow-lg hover:shadow-amber-500/5'}`}>
          <div className="flex items-center justify-between mb-3">
            <span className={`text-sm font-black ${isRunning ? 'text-muted-foreground' : 'text-amber-600'}`}>🔥 클래식 마틴</span>
            <Button size="sm" variant="ghost" disabled={isRunning} onClick={() => applyPreset('martingale')} className={`h-7 px-3 rounded-lg font-black text-[10px] ${isRunning ? 'bg-muted text-muted-foreground' : 'bg-amber-600 text-white hover:bg-amber-700'}`}>
              적용
            </Button>
          </div>
          <div className={`space-y-1.5 border-t pt-3 ${isRunning ? 'border-muted' : 'border-amber-500/5'}`}>
            <div className="flex items-center gap-1.5 text-[9px] font-bold text-muted-foreground uppercase"><Info className="w-3 h-3 text-amber-400" /> 설정 변경 내역:</div>
            <ul className="grid grid-cols-1 gap-1">
              <li className="text-[9px] font-medium text-foreground/80 flex items-center gap-1.5">✅ 물타기 2.0배 / 7차수</li>
              <li className="text-[9px] font-medium text-foreground/80 flex items-center gap-1.5">✅ 모든 추세/정밀 필터 비활성</li>
              <li className="text-[9px] font-medium text-foreground/80 flex items-center gap-1.5">✅ 빠른 탈출 위주 공격적 운용</li>
              <li className="text-[9px] font-medium text-foreground/80 flex items-center gap-1.5">✅ ROE 1.0% 고정 익절</li>
            </ul>
          </div>
        </div>

        {/* Preset 3: Precision Scalping */}
        <div className={`group relative flex flex-col bg-background border rounded-2xl p-4 transition-all ${isRunning ? 'border-muted opacity-80 cursor-not-allowed' : 'border-emerald-500/20 hover:border-emerald-500/50 hover:shadow-lg hover:shadow-emerald-500/5'}`}>
          <div className="flex items-center justify-between mb-3">
            <span className={`text-sm font-black ${isRunning ? 'text-muted-foreground' : 'text-emerald-600'}`}>⚡ 정밀 스캘핑</span>
            <Button size="sm" variant="ghost" disabled={isRunning} onClick={() => applyPreset('scalping')} className={`h-7 px-3 rounded-lg font-black text-[10px] ${isRunning ? 'bg-muted text-muted-foreground' : 'bg-emerald-600 text-white hover:bg-emerald-700'}`}>
              적용
            </Button>
          </div>
          <div className={`space-y-1.5 border-t pt-3 ${isRunning ? 'border-muted' : 'border-emerald-500/5'}`}>
            <div className="flex items-center gap-1.5 text-[9px] font-bold text-muted-foreground uppercase"><Info className="w-3 h-3 text-emerald-400" /> 설정 변경 내역:</div>
            <ul className="grid grid-cols-1 gap-1">
              <li className="text-[9px] font-medium text-foreground/80 flex items-center gap-1.5">✅ 물타기 1.3배 / 15차수</li>
              <li className="text-[9px] font-medium text-foreground/80 flex items-center gap-1.5">✅ 짧은 0.5% 간격 촘촘한 물타기</li>
              <li className="text-[9px] font-medium text-foreground/80 flex items-center gap-1.5">✅ RSI 30 기반 극단적 저점 포착</li>
              <li className="text-[9px] font-medium text-foreground/80 flex items-center gap-1.5">✅ 고배율 스캘핑 최적화</li>
            </ul>
          </div>
        </div>

        {/* Preset 4: Rebound Shield */}
        <div className={`group relative flex flex-col bg-background border rounded-2xl p-4 transition-all ${isRunning ? 'border-muted opacity-80 cursor-not-allowed' : 'border-blue-500/20 hover:border-blue-500/50 hover:shadow-lg hover:shadow-blue-500/5'}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex flex-col">
              <span className={`text-sm font-black ${isRunning ? 'text-muted-foreground' : 'text-blue-600'}`}>🛡️ 반등 쉴드</span>
            </div>
            <Button size="sm" variant="ghost" disabled={isRunning} onClick={() => applyPreset('rebound_shield')} className={`h-7 px-3 rounded-lg font-black text-[10px] ${isRunning ? 'bg-muted text-muted-foreground' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
              적용
            </Button>
          </div>
          <div className={`space-y-1.5 border-t pt-3 ${isRunning ? 'border-muted' : 'border-blue-500/5'}`}>
            <div className="flex items-center gap-1.5 text-[9px] font-bold text-muted-foreground uppercase"><Info className="w-3 h-3 text-blue-400" /> 설정 변경 내역:</div>
            <ul className="grid grid-cols-1 gap-1">
              <li className="text-[9px] font-medium text-foreground/80 flex items-center gap-1.5">✅ 물타기 1.15배 (소배율 유지)</li>
              <li className="text-[9px] font-medium text-foreground/80 flex items-center gap-1.5">✅ 반등 물량 덜어내기 (2차 이상, 0.5%)</li>
              <li className="text-[9px] font-medium text-foreground/80 flex items-center gap-1.5">✅ 트레일링 익절 (0.2% 콜백) 활성</li>
              <li className="text-[9px] font-medium text-foreground/80 flex items-center gap-1.5">✅ 4h 슈퍼 MA + RSI 과매도 필터</li>
              <li className="text-[9px] font-medium text-foreground/80 flex items-center gap-1.5">✅ 나씨tv  5분봉3틱룰 참고</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
